import { storeToRefs } from "pinia";

import { useHotKeys } from "@/composables/hotkeys";
import { useStekManager } from "@/composables/stek-manager";
import { useAskStore } from "@/stores/ask-store";
import { useUserStore } from "@/stores/user-store";

/**
 * Who owns ⌘K.
 *
 * There can only be one registration: the hotkey registry is first-mount-wins
 * and a duplicate silently no-ops (`composables/hotkeys/index.ts`), with no way
 * to override or unregister. So the decision cannot be split across the two
 * components that want the key — it has to be made in one place, here.
 *
 * The ask box takes it when it is available, and search keeps it otherwise.
 * That fallback is the whole reason this is safe to land now: the ask box is
 * behind a flag that is off in every deployed environment, so ⌘K today behaves
 * exactly as it always has. Search also keeps its toolbar click trigger either
 * way, so nothing is unreachable while where-search-lives is still being
 * settled.
 */
export const useCommandK = (openSearch: () => void) => {
    const { addHotKey } = useHotKeys();
    const { askBoxEnabled } = storeToRefs(useUserStore());
    const { stek } = useStekManager();
    const ask = useAskStore();

    addHotKey(
        "ControlOrMeta+k",
        (event) => {
            event.preventDefault();
            // The box asks about a stek and the endpoint requires one in the
            // header, so on an org-level page there is nothing for it to answer
            // about — the request would hang for the client's stek timeout and
            // then throw. Search has the same requirement, so falling through
            // leaves those pages behaving exactly as they do today.
            if (askBoxEnabled.value && stek.value) {
                ask.open();
            } else {
                openSearch();
            }
        },
        "Ask about this stek",
        "Global"
    );
};
